import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { PaymentProcessing } from "@/components/PaymentProcessing";
import { ShieldCheck, Smartphone, CheckCircle2, MessageSquare, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Toggle back to true once a dedicated A2P campaign for 2FA / password-reset SMS is approved
const SHOW_2FA = false;

export default function Security() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState(user?.phone || "");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const isVendor = user?.role === "subscriber";

  const closeAccount = async () => {
    setClosing(true);
    try {
      await api.post("/account/close", { origin_url: window.location.origin });
      toast.success("Your account is now closed. Check your email for a one-click reactivation link, or log back in within 90 days.");
      navigate("/", { replace: true });
      await logout();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setClosing(false); }
  };

  const verified = !!user?.phone_verified;
  const twoFA = !!user?.two_factor_enabled;
  const smsConsent = !!user?.sms_consent;
  const hasPhone = !!(user?.phone || "").trim();

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone });
      setSent(true);
      if (data.dev_code) toast.info(`Dev mode: your code is ${data.dev_code}`);
      else toast.success("Verification code sent via SMS.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/verify", { code });
      refreshUser(data.user);
      setSent(false); setCode("");
      toast.success("Phone verified!");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle2FA = async (enabled) => {
    try {
      const { data } = await api.post("/auth/2fa/toggle", { enabled });
      refreshUser(data.user);
      toast.success(enabled ? "Two-factor authentication enabled." : "Two-factor authentication disabled.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const toggleSmsConsent = async (enabled) => {
    try {
      const { data } = await api.put("/me/sms-consent", { enabled });
      refreshUser(data.user);
      toast.success(enabled ? "You're opted in to text notifications." : "You've opted out of text notifications.");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex items-center gap-2 text-primary"><ShieldCheck className="w-5 h-5" /><span className="text-xs uppercase tracking-[0.2em] font-bold">Settings</span></div>
      <h1 className="font-display text-4xl font-semibold tracking-tighter mt-2">Settings</h1>
      <p className="text-muted-foreground mt-2">Manage your phone, notifications{isVendor ? ", payment processing" : ""} and account.</p>

      {/* Phone */}
      <div className="mt-8 bg-white border border-border rounded-sm p-6">
        <div className="flex items-center gap-2"><Smartphone className="w-5 h-5" /><h2 className="font-display text-xl font-medium">Phone number</h2>
          {verified && <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-sm bg-green-100 text-green-800 font-medium"><CheckCircle2 className="w-3.5 h-3.5" />verified</span>}
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <Label htmlFor="phone">Number (E.164)</Label>
            <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+14155552671" className="mt-1.5 rounded-sm" data-testid="security-phone" disabled={sent} />
          </div>
          {!sent ? (
            <Button onClick={sendCode} disabled={busy || !phone} className="rounded-sm" data-testid="send-code-btn">
              {verified ? "Change / re-verify" : "Send verification code"}
            </Button>
          ) : (
            <div className="space-y-3">
              <div>
                <Label htmlFor="code">Enter the 6-digit code</Label>
                <Input id="code" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} inputMode="numeric"
                  placeholder="123456" className="mt-1.5 rounded-sm tracking-[0.4em] text-center max-w-[200px]" data-testid="security-code" />
              </div>
              <div className="flex gap-2">
                <Button onClick={verify} disabled={busy || code.length < 6} className="rounded-sm" data-testid="verify-code-btn">Verify</Button>
                <Button variant="outline" onClick={() => { setSent(false); setCode(""); }} className="rounded-sm">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 2FA — temporarily hidden until a separate A2P campaign for 2FA / password-reset is approved */}
      {SHOW_2FA && (
      <div className="mt-6 bg-white border border-border rounded-sm p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-medium">Two-factor authentication</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {verified ? "Require an SMS code at login for extra security." : "Verify your phone first to enable 2FA."}
            </p>
          </div>
          <Switch checked={twoFA} disabled={!verified} onCheckedChange={toggle2FA} data-testid="twofa-toggle" />
        </div>
      </div>
      )}

      {/* SMS notifications opt-in/out */}
      <div className="mt-6 bg-white border border-border rounded-sm p-6" data-testid="sms-consent-card">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              <h2 className="font-display text-xl font-medium">Text (SMS) notifications</h2>
              <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${smsConsent ? "bg-green-100 text-green-800" : "bg-neutral-200 text-neutral-600"}`} data-testid="sms-consent-status">
                {smsConsent ? "opted in" : "opted out"}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {hasPhone
                ? "Yes, sign me up! Text me booking updates, appointment reminders."
                : "Add a mobile number above to receive text notifications."}
            </p>
          </div>
          <Switch checked={smsConsent} disabled={!hasPhone} onCheckedChange={toggleSmsConsent} data-testid="sms-consent-toggle" />
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mt-3">
          By opting in you agree to receive recurring automated texts from BookdHUB (booking
          confirmations and appointment reminders). Consent isn't a condition of purchase.
          Msg frequency varies. Msg &amp; data rates may apply. Reply <b>HELP</b> for help, <b>STOP</b> to cancel.
          See <Link to="/terms" className="text-primary underline" data-testid="security-terms-link">Terms of Service</Link> &amp;{" "}
          <Link to="/privacy" className="text-primary underline" data-testid="security-privacy-link">Privacy Policy</Link>.
        </p>
      </div>

      {/* Payment processing — providers only */}
      {isVendor && <PaymentProcessing />}

      {/* Danger zone — close account */}
      <div className="mt-6 bg-white border border-red-300 rounded-sm p-6" data-testid="close-account-card">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <h2 className="font-display text-xl font-medium text-red-900">Close account</h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Your account stays dormant for <b>90 days</b> — log back in any time within that window to
          reactivate it exactly as you left it. After 90 days it can no longer be reactivated.
          {isVendor && " Your profile and services are hidden from search while dormant, and you must complete or cancel any booked services before closing."}
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="rounded-sm mt-4" data-testid="close-account-btn">Close my account</Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="close-account-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Close your account?</AlertDialogTitle>
              <AlertDialogDescription>
                You'll be signed out immediately. Your account will stay dormant for 90 days —
                logging back in within that time restores everything.
                {isVendor && " If you have upcoming booked services, you'll need to complete or cancel them first."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="close-account-cancel">Keep my account</AlertDialogCancel>
              <AlertDialogAction onClick={closeAccount} disabled={closing} className="bg-red-600 hover:bg-red-700" data-testid="close-account-confirm">
                {closing ? "Closing…" : "Yes, close it"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
